import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Mail, User, Phone, ChevronRight } from "lucide-react";

export default function WelcomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-green-900 to-lime-800 text-white overflow-hidden">
      <style>{`
        @keyframes cupPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.15); }
          50% { box-shadow: 0 0 0 14px rgba(255,255,255,0); }
        }
      `}</style>
      <GolfLanding />
    </div>
  );
}

function GolfLanding() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(() => {
    return form.name.trim() && form.email.trim() && form.phone.trim();
  }, [form]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setError("Please complete your name, email, and phone number.");
      return;
    }
    // Basic email validation
    if (!form.email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setSubmitted(true);
    // Show welcome modal after animation completes
    setTimeout(() => setShowWelcome(true), 2300);
  };

  const handleContinue = () => {
    // Store form data for pre-filling signup
    sessionStorage.setItem('welcomeFormData', JSON.stringify(form));
    navigate('/?signup=true');
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 py-10">
      {/* Background effects */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.22),_transparent_35%)]" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.12),_transparent_25%)]" />
      </div>

      <div className="relative z-10 grid lg:grid-cols-2 gap-6 lg:gap-8 w-full max-w-7xl items-center">
        {/* Left Panel - Form */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl order-2 lg:order-1"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium border border-white/15">
            <Trophy className="w-4 h-4 text-lime-300" /> Exclusive Membership Experience
          </div>

          <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
            Land Your <span className="text-lime-300">Hole in One</span>
          </h1>

          <p className="mt-5 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl">
            Enter your information to unlock an interactive golf moment and receive your welcome into
            <span className="font-semibold text-lime-200"> The Bundle Golf Club USA</span>.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 sm:mt-8 space-y-4">
            <Field
              icon={<User className="w-5 h-5" />}
              placeholder="Your full name"
              value={form.name}
              onChange={(value) => setForm((prev) => ({ ...prev, name: value }))}
              disabled={submitted}
            />
            <Field
              icon={<Mail className="w-5 h-5" />}
              placeholder="Your email address"
              type="email"
              value={form.email}
              onChange={(value) => setForm((prev) => ({ ...prev, email: value }))}
              disabled={submitted}
            />
            <Field
              icon={<Phone className="w-5 h-5" />}
              placeholder="Your phone number"
              type="tel"
              value={form.phone}
              onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))}
              disabled={submitted}
            />

            {error && <p className="text-red-300 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={submitted}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-2xl bg-lime-300 text-green-950 font-bold px-6 py-4 text-lg shadow-xl hover:bg-lime-200 hover:scale-[1.01] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitted ? "Taking the shot..." : "Take the Shot"} 
              <ChevronRight className="w-5 h-5" />
            </button>
          </form>

          <p className="mt-6 text-sm text-white/50 text-center">
            Already a member?{" "}
            <a href="/" className="text-lime-300 hover:text-lime-200 underline">
              Sign in here
            </a>
          </p>
        </motion.div>

        {/* Right Panel - Golf Animation */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative min-h-[400px] sm:min-h-[480px] lg:min-h-[520px] rounded-3xl border border-white/15 bg-white/8 backdrop-blur-xl shadow-2xl overflow-hidden order-1 lg:order-2"
        >
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))]" />
          <div className="absolute inset-x-0 top-0 h-48 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.25),_transparent_55%)]" />

          <div className="relative h-full p-6 lg:p-8 flex flex-col justify-between">
            <div>
              <p className="text-white/70 uppercase tracking-[0.25em] sm:tracking-[0.35em] text-xs">Interactive Welcome</p>
              <h2 className="mt-3 text-xl sm:text-2xl lg:text-3xl font-semibold">Your shot unlocks the club</h2>
            </div>

            {/* Golf Course Scene */}
            <div className="relative h-[280px] sm:h-[320px] lg:h-[340px] rounded-[2rem] bg-gradient-to-b from-sky-300/30 to-green-600/70 border border-white/10 overflow-hidden">
              {/* Sky */}
              <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-sky-400/40 to-transparent" />
              
              {/* Sun */}
              <div className="absolute top-6 right-8 w-12 h-12 rounded-full bg-yellow-200/60 blur-sm" />
              
              {/* Distant trees */}
              <div className="absolute bottom-32 left-4 w-8 h-16 bg-green-800/40 rounded-full blur-sm" />
              <div className="absolute bottom-36 left-10 w-6 h-12 bg-green-700/30 rounded-full blur-sm" />
              <div className="absolute bottom-34 right-20 w-10 h-20 bg-green-800/30 rounded-full blur-sm" />
              
              {/* Fairway highlight */}
              <div className="absolute left-6 sm:left-8 right-6 sm:right-8 bottom-12 sm:bottom-14 h-4 bg-lime-200/70 rounded-full blur-sm" />
              
              {/* Green */}
              <div className="absolute left-6 sm:left-8 right-6 sm:right-8 bottom-8 sm:bottom-10 h-24 sm:h-28 bg-green-500/60 rounded-[100px]" />

              {/* Hole / Cup with shadow */}
              <div className="absolute right-10 sm:right-12 bottom-10 sm:bottom-12 w-8 sm:w-9 h-3 bg-black/40 rounded-full blur-[2px]" />
              <div
                className="absolute right-10 sm:right-12 bottom-10 sm:bottom-12 w-6 sm:w-7 h-6 sm:h-7 rounded-full bg-black/90 border-2 border-white/20"
                style={{ animation: submitted ? "cupPulse 0.9s ease-in-out 2" : "none" }}
              />
              
              {/* Flag pole */}
              <div className="absolute right-[54px] sm:right-[66px] bottom-[32px] sm:bottom-[35px] w-1 sm:w-1.5 h-16 sm:h-20 bg-white/90 rounded-full shadow-lg" />
              
              {/* Flag */}
              <div 
                className="absolute right-[42px] sm:right-[52px] bottom-[78px] sm:bottom-[92px]"
              >
                <div className="w-8 h-6 bg-red-500 rounded-sm shadow-md" style={{ clipPath: 'polygon(0 0, 100% 50%, 0 100%)' }} />
              </div>

              {/* Golfer SVG */}
              <motion.g
                initial={false}
                animate={submitted ? "putting" : "ready"}
              >
                <svg 
                  className="absolute left-8 sm:left-16 bottom-8 sm:bottom-10" 
                  width="120" 
                  height="140" 
                  viewBox="0 0 120 140"
                >
                  {/* Golfer Shadow */}
                  <ellipse cx="55" cy="135" rx="25" ry="5" fill="rgba(0,0,0,0.2)" />
                  
                  {/* Golfer Body Group */}
                  <g>
                    {/* Legs */}
                    <motion.path
                      d="M45 95 L40 130 L35 132"
                      stroke="#1e3a5f"
                      strokeWidth="8"
                      strokeLinecap="round"
                      fill="none"
                    />
                    <motion.path
                      d="M55 95 L58 130 L63 132"
                      stroke="#1e3a5f"
                      strokeWidth="8"
                      strokeLinecap="round"
                      fill="none"
                    />
                    
                    {/* Shoes */}
                    <ellipse cx="35" cy="134" rx="8" ry="4" fill="#2d2d2d" />
                    <ellipse cx="63" cy="134" rx="8" ry="4" fill="#2d2d2d" />
                    
                    {/* Torso */}
                    <motion.path
                      d="M50 55 Q45 75 50 95"
                      stroke="#dc2626"
                      strokeWidth="20"
                      strokeLinecap="round"
                      fill="none"
                      animate={submitted ? { d: "M50 55 Q48 75 50 95" } : { d: "M50 55 Q45 75 50 95" }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    {/* Head */}
                    <circle cx="50" cy="40" r="18" fill="#fcd9b6" />
                    
                    {/* Cap */}
                    <path d="M32 38 Q50 25 68 38" fill="#1e40af" />
                    <ellipse cx="50" cy="38" rx="20" ry="6" fill="#1e40af" />
                    <rect x="65" y="32" width="12" height="6" rx="2" fill="#1e40af" />
                    
                    {/* Face details */}
                    <circle cx="44" cy="38" r="2" fill="#333" />
                    <circle cx="56" cy="38" r="2" fill="#333" />
                    <path d="M46 46 Q50 49 54 46" stroke="#333" strokeWidth="1.5" fill="none" />
                    
                    {/* Arms and Putter */}
                    <motion.g
                      animate={submitted ? { rotate: -25 } : { rotate: 0 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      style={{ originX: "50px", originY: "65px" }}
                    >
                      {/* Left arm */}
                      <path
                        d="M40 60 L25 85 L30 95"
                        stroke="#fcd9b6"
                        strokeWidth="6"
                        strokeLinecap="round"
                        fill="none"
                      />
                      {/* Right arm */}
                      <path
                        d="M60 60 L70 80 L30 95"
                        stroke="#fcd9b6"
                        strokeWidth="6"
                        strokeLinecap="round"
                        fill="none"
                      />
                      
                      {/* Glove */}
                      <circle cx="30" cy="95" r="5" fill="#fff" />
                      
                      {/* Putter shaft */}
                      <motion.line
                        x1="30"
                        y1="95"
                        x2="75"
                        y2="125"
                        stroke="#888"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                      
                      {/* Putter head */}
                      <motion.rect
                        x="70"
                        y="122"
                        width="20"
                        height="8"
                        rx="2"
                        fill="#555"
                        transform="rotate(-15, 75, 125)"
                      />
                    </motion.g>
                  </g>
                </svg>
              </motion.g>

              {/* Golf Ball */}
              <motion.div
                initial={false}
                animate={submitted ? { x: 130, y: 2, scale: 0.9 } : { x: 0, y: 0, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="absolute left-[115px] sm:left-[145px] bottom-[42px] sm:bottom-[48px] w-5 sm:w-6 h-5 sm:h-6 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
              >
                {/* Ball dimples effect */}
                <div className="absolute inset-1 rounded-full bg-gradient-to-br from-white to-gray-100" />
              </motion.div>

              {/* Ball trail effect when putting */}
              <AnimatePresence>
                {submitted && (
                  <motion.div
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: [0, 0.5, 0], scaleX: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="absolute left-[120px] sm:left-[150px] bottom-[44px] sm:bottom-[50px] w-20 h-1 bg-gradient-to-r from-white/50 to-transparent rounded-full origin-left"
                  />
                )}
              </AnimatePresence>

              {/* Success message */}
              <AnimatePresence>
                {submitted && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 1.5, duration: 0.5 }}
                    className="absolute left-4 sm:left-8 top-4 sm:top-8 rounded-2xl bg-black/40 backdrop-blur-md border border-white/15 px-3 sm:px-4 py-2 sm:py-3"
                  >
                    <p className="text-xs sm:text-sm text-white/80">Perfect putt...</p>
                    <p className="font-semibold text-lime-300 text-sm sm:text-base">It's in the hole!</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Welcome Modal */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6 bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.88, y: 18 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.35 }}
              className="w-full max-w-xl rounded-[2rem] border border-white/15 bg-gradient-to-br from-green-950 to-emerald-900 p-6 sm:p-8 shadow-2xl"
            >
              <div className="mx-auto mb-5 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-lime-300 text-green-950">
                <Trophy className="w-7 h-7 sm:w-8 sm:h-8" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-center">
                Welcome to The Bundle Golf Club USA
              </h3>
              <p className="mt-4 text-center text-white/80 text-base sm:text-lg leading-relaxed">
                {form.name ? (
                  <>
                    <span className="text-lime-300 font-semibold">{form.name}</span>, your hole in one has unlocked your welcome experience.
                  </>
                ) : (
                  "Your hole in one has unlocked your welcome experience."
                )}
              </p>
              <p className="mt-2 text-center text-white/60 text-sm">
                You're one step away from joining the most exclusive golf community.
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowWelcome(false)}
                  className="flex-1 rounded-2xl border border-white/20 px-5 py-3 sm:py-4 font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  Replay Shot
                </button>
                <button 
                  onClick={handleContinue}
                  className="flex-1 rounded-2xl bg-lime-300 px-5 py-3 sm:py-4 font-bold text-green-950 hover:bg-lime-200 hover:scale-[1.01] transition-all"
                >
                  Continue to Membership
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({ icon, placeholder, value, onChange, type = "text", disabled = false }) {
  return (
    <div className={`flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 sm:py-4 shadow-sm transition-colors ${disabled ? 'opacity-60' : 'focus-within:border-lime-300/50 focus-within:bg-white/15'}`}>
      <div className="text-white/70">{icon}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full bg-transparent outline-none placeholder:text-white/50 text-white disabled:cursor-not-allowed"
      />
    </div>
  );
}
